import { useEffect, useRef, useState } from "react";
import { MapPin, Clock, Phone, MessageCircle, Train, Car, Footprints, Quote, ExternalLink, ChevronDown, ClipboardList, Gauge, Search, Route,
  ClipboardCheck, Trophy, Star, Check,
  Target, Activity, HeartPulse, Dumbbell, Bike, Timer, ShieldCheck, Sparkles, Move, Award, UserCheck, BadgeCheck, Layers, CalendarCheck } from "lucide-react";
import { FreeChat, Availability } from "@/components/landing/Conversion";
import { ResultStat } from "@/components/landing/Proof";

const ICONS = { Target, Activity, Footprints, HeartPulse, Dumbbell, Bike, Timer, ShieldCheck, Sparkles, Move, Award, UserCheck };
const CYCLE = [Target, Activity, HeartPulse, Dumbbell, Timer, Move];
const HOW_ICONS = [MessageCircle, ClipboardCheck, HeartPulse, Trophy];
const REVIEW_BLUE = "#a9d3f5"; // light tint of brand blue #247cc1: 4.8:1 on the review card (WCAG AA)
const H2 = ({ children, light }) => <h2 className={`lp-display mt-2 text-3xl font-bold md:text-4xl ${light ? "text-white" : "text-slate-900"}`}>{children}</h2>;

const AutoVideo = ({ v }) => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || !("IntersectionObserver" in window)) return undefined;
    const io = new IntersectionObserver(([e]) => (e.isIntersecting ? el.play().catch(() => {}) : el.pause()), { threshold: 0.35 });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return <video ref={ref} src={v.src} poster={v.poster} muted playsInline loop preload="none" className="aspect-[9/16] w-full rounded-2xl bg-slate-200 object-cover shadow-lg" />;
};

export const VideoSection = ({ videos, t, color }) => (
  <section className="mx-auto grid max-w-6xl items-center gap-10 px-5 pt-16 md:grid-cols-2" data-testid="lp-video-section">
    <div>
      <div className="text-sm font-semibold uppercase tracking-wider" style={{ color }}>{t.video_subheading}</div>
      <h2 className="lp-display mt-2 text-4xl font-extrabold text-slate-900 md:text-5xl">{t.video_heading}</h2>
      <p className="mt-5 text-lg leading-relaxed text-slate-600">{t.video_body}</p>
    </div>
    {videos.length > 0 && (
      <div className={`grid gap-4 ${videos.length > 1 ? "grid-cols-2" : "mx-auto w-2/3 grid-cols-1"}`} data-testid="lp-videos">
        {videos.map((v) => <AutoVideo key={v.src} v={v} />)}
      </div>
    )}
  </section>
);

const ASSESS_ICONS = [ClipboardList, Gauge, Search, Route];
const CARD = "rounded-2xl bg-white shadow-sm ring-1 ring-slate-200/70 transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-lg";

export const Reveal = ({ children, className = "", as: Tag = "div", ...rest }) => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || !("IntersectionObserver" in window)) return el?.classList.add("is-in");
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { el.classList.add("is-in"); io.disconnect(); } }, { threshold: 0.12, rootMargin: "0px 0px -40px 0px" });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return <Tag ref={ref} className={`lp-reveal ${className}`} {...rest}>{children}</Tag>;
};

export const Img = ({ img, alt, className = "", testid }) => img ? (
  <img src={img.src} width={img.w} height={img.h} alt={alt} loading="lazy" decoding="async" data-testid={testid}
    className={`w-full rounded-2xl object-cover shadow-[0_18px_40px_-18px_rgba(15,23,42,0.45)] ${className}`} />
) : null;

const ImageStack = ({ images, alt, testid }) => (
  <div className="space-y-5 md:sticky md:top-24" data-testid={testid}>
    {images.map((im, i) => <Img key={im.src} img={im} alt={`${alt} ${i + 1}`} testid={`${testid}-${i}`} />)}
  </div>
);

export const Assessment = ({ c, color, images = [] }) => {
  const cards = (c.assessment_cards || []).slice(0, 4);
  if (!c.offer_title && !cards.length) return null;
  return (
    <section className="mx-auto max-w-6xl px-5 py-14 md:py-20" data-testid="lp-assessment">
      <Reveal>
        <div className="text-sm font-semibold uppercase tracking-wider" style={{ color }}>Your assessment</div>
        <H2>{c.offer_title}</H2>
        {c.offer_text && <p className="mt-4 max-w-3xl text-lg leading-relaxed text-slate-600">{c.offer_text}</p>}
      </Reveal>
      <div className="mt-10 grid items-start gap-8 md:grid-cols-2">
        <div className="space-y-5" data-testid="lp-assessment-grid">
          {cards.map((k, i) => {
            const Icon = ASSESS_ICONS[i % ASSESS_ICONS.length];
            return (
              <Reveal key={k.title} data-testid={`lp-assessment-card-${i}`} className={`${CARD} border-l-4 p-6 md:p-7`} style={{ borderLeftColor: color }}>
                <div className="flex items-center gap-3">
                  <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full font-bold text-white" style={{ background: color }}>{i + 1}</span>
                  <Icon className="h-6 w-6 shrink-0" strokeWidth={1.5} style={{ color }} data-testid={`lp-assessment-icon-${i}`} />
                  <h3 className="lp-display text-xl font-bold text-slate-900">{k.title}</h3>
                </div>
                {k.bullets?.length ? (
                  <ul className="mt-4 space-y-2.5" data-testid={`lp-assessment-bullets-${i}`}>{k.bullets.map((b) => (
                    <li key={b} className="flex gap-3 text-base leading-snug text-slate-700 md:text-[17px]"><Check className="mt-0.5 h-5 w-5 shrink-0" strokeWidth={2.25} style={{ color }} />{b}</li>
                  ))}</ul>
                ) : <p className="mt-3 text-lg leading-relaxed text-slate-600">{k.text}</p>}
              </Reveal>
            );
          })}
        </div>
        <ImageStack images={images} alt="Assessment at The Rehab Lab" testid="lp-assessment-images" />
      </div>
    </section>
  );
};

export const Benefits = ({ items, color, images = [] }) => items.length ? (
  <section className="mx-auto max-w-6xl px-5 py-14 md:py-20" data-testid="lp-benefits">
    <div className="grid items-start gap-8 md:grid-cols-[1.4fr_1fr]">
      <div className="space-y-5">
        {items.map((b, i) => {
          const Icon = ICONS[b.icon] || CYCLE[i % CYCLE.length];
          const bullets = b.bullets || (b.text ? [b.text] : []);
          return (
            <Reveal key={b.title} className={`${CARD} flex gap-5 p-6`}>
              <Icon className="h-8 w-8 shrink-0" strokeWidth={1.4} style={{ color }} />
              <div>
                <h3 className="lp-display text-xl font-bold text-slate-900">{b.title}</h3>
                <ul className="mt-2 space-y-1.5">{bullets.map((x) => <li key={x} className="flex gap-2 text-slate-600"><span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full" style={{ background: color }} />{x}</li>)}</ul>
              </div>
            </Reveal>
          );
        })}
      </div>
      <ImageStack images={images} alt="Treatment at The Rehab Lab" testid="lp-benefit-images" />
    </div>
  </section>
) : null;

const LONG_REVIEW = 420;
const ReviewCard = ({ t, i }) => {
  const [open, setOpen] = useState(false);
  const long = t.quote.length > LONG_REVIEW;
  return (
    <Reveal as="figure" data-testid={`lp-review-${i}`} className="flex flex-col rounded-2xl bg-white/10 p-6 text-white transition-transform duration-300 hover:-translate-y-1"><Quote className="h-6 w-6 opacity-60" />{t.title && <div className="lp-display mt-3 text-lg font-bold italic" style={{ color: REVIEW_BLUE }} data-testid={`lp-review-title-${i}`}>“{t.title}”</div>}
      <blockquote className={`mt-2 text-white/85 ${long && !open ? "line-clamp-[7]" : ""}`} data-testid={`lp-review-quote-${i}`}>{t.quote}</blockquote>
      {long && <button type="button" onClick={() => setOpen(!open)} aria-expanded={open} data-testid={`lp-review-more-${i}`} className="mt-2 self-start text-sm font-semibold underline-offset-4 hover:underline" style={{ color: REVIEW_BLUE }}>{open ? "Show less" : "Read more"}</button>}
      {(t.author || t.label) && <figcaption className="mt-auto pt-4 text-sm font-semibold">{t.author || t.label}{t.source === "Google review" && <span className="ml-2 text-xs font-normal text-white/60">· Google review</span>}</figcaption>}</Reveal>
  );
};

export const Reviews = ({ items, dark, preview, footer, stat }) => items.length ? (
  <section className="py-14 md:py-20" style={{ background: dark }} data-testid="lp-testimonials"><div className="mx-auto max-w-6xl px-5">
    <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
      <div>
        <div className="text-sm font-semibold uppercase tracking-wider" style={{ color: REVIEW_BLUE }} data-testid="lp-reviews-eyebrow">Hear from our clients</div>
        <H2 light>What our clients say</H2>
      </div>
      <ResultStat stat={stat} light testid="lp-reviews-stat" />
    </div>
    <div className={`mt-8 grid items-start gap-5 ${items.length > 1 ? "md:grid-cols-3" : "max-w-xl"}`}>{items.slice(0, 3).map((t, i) => <ReviewCard key={t.quote} t={t} i={i} />)}</div>
    {footer}
  </div></section>
) : preview ? (
  <section className="mx-auto max-w-6xl px-5 py-10" data-testid="lp-testimonials-placeholder"><div className="rounded-2xl border-2 border-dashed border-slate-300 p-8 text-center text-slate-500">No matching long-form review found for this service (hidden on the live page).</div></section>
) : null;

const JourneyPill = ({ testid }) => (
  <a href="#book" data-testid={testid}
    onClick={(e) => { const el = document.getElementById("book"); if (el) { e.preventDefault(); el.scrollIntoView({ behavior: "smooth", block: "start" }); } }}
    className="inline-flex items-center gap-2 rounded-full bg-[#247cc1] px-[22px] py-3 text-base font-semibold text-white shadow-[0_10px_24px_-12px_rgba(36,124,193,0.8)] transition-[transform,background-color,box-shadow] duration-200 hover:-translate-y-0.5 hover:bg-[#1d6aa6] hover:shadow-[0_14px_28px_-12px_rgba(36,124,193,0.9)] active:translate-y-0 active:scale-[0.98] md:text-[17px]">
    <Footprints className="h-5 w-5" strokeWidth={2} />Start your journey today<span aria-hidden="true">→</span>
  </a>
);
const SPLIT_H2 = "lp-display text-3xl font-bold text-slate-900 md:text-[40px] md:leading-tight";
const Split = ({ title, eyebrow, image, alt, children, testid }) => (
  <section className="mx-auto grid max-w-6xl items-center gap-10 px-5 py-14 md:grid-cols-2 md:py-20" data-testid={testid}>
    <Reveal>{eyebrow}<h2 className={`${SPLIT_H2} ${eyebrow ? "mt-5" : ""}`}>{title}</h2><div className="mt-7">{children}</div></Reveal>
    {image && <Reveal><Img img={image} alt={alt} className="aspect-[536/526] h-auto" testid={`${testid}-image`} /></Reveal>}
  </section>
);

export const HowItWorks = ({ steps, image, color }) => (
  <Split title="How it works" eyebrow={<JourneyPill testid="lp-how-eyebrow" />} image={image} alt="How it works at The Rehab Lab" testid="lp-how-it-works">
    <ol className="space-y-7">{steps.map((h, i) => {
      const Icon = HOW_ICONS[i % HOW_ICONS.length];
      return (
        <li key={h.step} className="group flex gap-5"><span className="grid h-10 w-10 shrink-0 place-items-center rounded-full font-bold text-white transition-transform duration-300 group-hover:scale-110" style={{ background: color }}>{i + 1}</span>
          <div><div className="flex items-center gap-2.5 text-[17px] font-semibold text-slate-900" data-testid={`lp-how-step-${i}`}><Icon className="h-5 w-5 shrink-0" strokeWidth={1.5} style={{ color }} data-testid={`lp-how-icon-${i}`} />{h.step.replace(/^\s*\d+[.)]\s*/, "")}</div><p className="mt-1 text-[15px] leading-relaxed text-slate-600">{h.text}</p></div></li>
      );
    })}</ol>
  </Split>
);

const WHY_ICONS = [[/ahpc|licensed/i, BadgeCheck], [/plus method/i, Layers], [/one-to-one/i, UserCheck], [/medication/i, Activity],
  [/years/i, Award], [/google review/i, Star], [/referral/i, CalendarCheck], [/insur/i, ShieldCheck]];
export const WhyUs = ({ items, image, color, intro, dark }) => (
  <section className="py-20 md:py-28" style={{ background: dark }} data-testid="lp-why-us">
  <div className="mx-auto grid max-w-6xl items-center gap-10 px-5 md:grid-cols-2 md:gap-14">
    <Reveal>
      <h2 className={`${SPLIT_H2} !text-white`}>Why The Rehab Lab?</h2>
      {intro && <p className="mt-3 text-lg font-semibold" style={{ color: REVIEW_BLUE }} data-testid="lp-why-intro">{intro}</p>}
      <ul className="mt-7 space-y-5" data-testid="lp-why-list">{items.map((w, i) => {
        const Icon = (WHY_ICONS.find(([rx]) => rx.test(w)) || [])[1] || CYCLE[i % CYCLE.length];
        return <li key={w} className="flex items-center gap-4" data-testid={`lp-why-item-${i}`}><span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-[#a9d3f5]/15 ring-1 ring-[#a9d3f5]/25"><Icon className="h-5 w-5" strokeWidth={1.5} style={{ color: REVIEW_BLUE }} /></span><span className="text-[17px] font-semibold text-white/95">{w}</span></li>;
      })}</ul>
    </Reveal>
    {image && <Reveal><Img img={image} alt="The Rehab Lab clinic" className="aspect-[4/5] h-auto shadow-[0_24px_60px_-20px_rgba(0,0,0,0.7)] ring-1 ring-white/15" testid="lp-why-us-image" /></Reveal>}
  </div>
  </section>
);

export const Plus = ({ p, color, wa, onWhatsApp }) => p ? (
  <section className="mx-auto max-w-6xl px-5 pt-14 md:pt-24" data-testid="lp-plus">
    <Reveal>
      <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">{p.eyebrow}</div>
      <h2 className="lp-display mt-4 text-[clamp(1.3rem,6.6vw,2.25rem)] font-bold leading-[1.1] text-slate-900 sm:text-5xl lg:text-6xl"><span className="block">{p.heading}</span><span className="block whitespace-nowrap" style={{ color }} data-testid="lp-plus-line2">{p.lead && <span className="font-normal" data-testid="lp-plus-lead">{p.lead} </span>}<span className="lp-serif font-normal italic" data-testid="lp-plus-accent">{p.accent}</span></span></h2>
      <p className="mt-6 max-w-3xl text-lg leading-relaxed text-slate-600 md:text-xl">{p.intro}</p>
    </Reveal>
    <div className="mt-12 grid border-y border-slate-200 md:grid-cols-3 md:divide-x md:divide-slate-200" data-testid="lp-plus-steps">
      {p.steps.map((s, i) => (
        <Reveal key={s.n} className="group border-b border-slate-200 py-8 last:border-b-0 md:border-b-0 md:px-8 md:first:pl-0" data-testid={`lp-plus-step-${i}`} style={{ transitionDelay: `${i * 90}ms` }}>
          <div className="lp-serif text-6xl font-light text-slate-300 transition-colors duration-300 group-hover:text-slate-400">{s.n}</div>
          <div className="mt-3 text-[15px] font-semibold uppercase tracking-[0.1em] md:text-[17px]" style={{ color }} data-testid={`lp-plus-label-${i}`}>{s.label}</div>
          <div className="mt-1.5 text-xl font-semibold text-slate-900" data-testid={`lp-plus-title-${i}`}>{s.title}</div>
          <p className="mt-2 leading-relaxed text-slate-600">{s.text}</p>
        </Reveal>
      ))}
    </div>
    <div className="mt-10 flex flex-wrap items-start gap-3">
      <a href="#book" data-testid="lp-plus-book" className="lp-btn rounded-full px-7 py-3.5 font-semibold text-white" style={{ background: color }}>Book my assessment</a>
      <a href={wa} onClick={onWhatsApp} target="_blank" rel="noreferrer" data-testid="lp-plus-whatsapp" className="lp-btn inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-7 py-3.5 font-semibold text-slate-900"><MessageCircle className="h-5 w-5 text-[#25D366]" /> WhatsApp me</a>
    </div>
  </section>
) : null;

const FaqItem = ({ q, open, onToggle, color, i }) => (
  <div className={`rounded-2xl bg-white shadow-[0_8px_24px_-14px_rgba(15,23,42,0.35)] ring-1 ring-slate-200/70 transition-[box-shadow,transform] duration-300 hover:-translate-y-0.5 hover:shadow-lg ${open ? "border-l-4" : "border-l-4 border-l-transparent"}`}
    style={open ? { borderLeftColor: color } : undefined} data-testid={`lp-faq-item-${i}`}>
    <button type="button" onClick={onToggle} aria-expanded={open} data-testid={`lp-faq-toggle-${i}`} className="flex w-full items-center justify-between gap-4 p-5 text-left">
      <span className="text-lg font-semibold text-slate-900">{q.q}</span>
      <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full transition-colors" style={{ background: open ? color : "#eef5fb", color: open ? "#fff" : color }}>
        <ChevronDown className={`h-4 w-4 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </span>
    </button>
    <div className={`grid transition-[grid-template-rows] duration-300 ease-out ${open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}>
      <div className="overflow-hidden"><p className="px-5 pb-5 leading-relaxed text-slate-600">{q.a}</p></div>
    </div>
  </div>
);

export const Team = ({ members, color }) => members.length ? (
  <section className="mx-auto max-w-6xl px-5 py-14 md:py-20" data-testid="lp-team">
    <Reveal className="text-center">
      <div className="text-sm font-semibold uppercase tracking-wider" style={{ color }}>Our team</div>
      <H2>Meet Our Team</H2>
      <p className="mt-3 text-lg text-slate-600">AHPC-registered physiotherapists and a sports therapist who treat you one-to-one.</p>
    </Reveal>
    <div className="-mx-5 mt-10 flex snap-x snap-mandatory gap-5 overflow-x-auto px-5 pb-4 sm:mx-0 sm:flex-wrap sm:justify-center sm:overflow-visible sm:px-0" data-testid="lp-team-grid">
      {members.map((m, i) => (
        <Reveal key={m.name} data-testid={`lp-team-card-${i}`} className={`${CARD} w-[78%] shrink-0 snap-center p-6 text-center sm:w-[calc(50%-10px)] lg:w-[calc(25%-15px)]`} style={{ transitionDelay: `${(i % 4) * 80}ms` }}>
          <img src={m.photo} alt={m.name} width="152" height="152" loading="lazy" decoding="async"
            className="mx-auto h-36 w-36 rounded-full object-cover ring-4 ring-offset-4 md:h-[152px] md:w-[152px]" style={{ "--tw-ring-color": `${color}40` }} />
          <div className="mt-5 text-lg font-bold text-slate-900">{m.name}</div>
          <div className="mt-1 text-xs font-semibold uppercase tracking-[0.16em]" style={{ color }}>{m.title}</div>
          <p className="lp-serif mt-4 line-clamp-3 text-[15px] italic leading-relaxed text-slate-600" title={m.quote}>“{m.quote}”</p>
        </Reveal>
      ))}
    </div>
  </section>
) : null;

export const Faq = ({ items, images = [], color, wa, onWhatsApp }) => {
  const [open, setOpen] = useState(0);
  if (!items.length) return null;
  return (
    <section className="mx-auto max-w-6xl px-5 py-14 md:py-20" data-testid="lp-faq">
      <div className="grid items-start gap-10 md:grid-cols-[1.4fr_1fr]">
        <Reveal>
          <H2>FAQs</H2>
          <div className="mt-8 space-y-4">{items.map((q, i) => <FaqItem key={q.q} q={q} i={i} color={color} open={open === i} onToggle={() => setOpen(open === i ? -1 : i)} />)}</div>
          <a href={wa} onClick={onWhatsApp} target="_blank" rel="noreferrer" data-testid="lp-faq-whatsapp" className="mt-8 inline-flex items-center gap-2 rounded-full px-5 py-3 font-semibold text-white transition-transform hover:-translate-y-0.5" style={{ background: "#25D366" }}>
            <MessageCircle className="h-5 w-5" /> Not sure? Chat with a physio for free
          </a>
        </Reveal>
        {images.length > 0 && <div className="space-y-5" data-testid="lp-faq-images">{images.map((im, i) => <Img key={im.src} img={im} alt={`The Rehab Lab physiotherapy ${i + 1}`} testid={i ? `lp-faq-image-${i}` : "lp-faq-image"} />)}</div>}
      </div>
    </section>
  );
};

const DIR_ICONS = [Train, Car, Footprints];

export const VisitUs = ({ studio, color, wa, freeWa, onWhatsApp, onCall }) => (
  <section className="bg-white py-16" data-testid="lp-visit-us"><div className="mx-auto grid max-w-6xl gap-10 px-5 md:grid-cols-2">
    <div>
      <JourneyPill testid="lp-visit-eyebrow" />
      <div className="mt-3"><H2>Visit the clinic</H2></div>
      <p className="mt-5 flex gap-2 text-lg text-slate-800"><MapPin className="mt-1 h-5 w-5 shrink-0" style={{ color }} />{studio.address}</p>
      <a href={studio.maps_url} target="_blank" rel="noreferrer" data-testid="lp-open-maps" className="mt-2 inline-flex items-center gap-1.5 pl-7 text-sm font-semibold" style={{ color }}>Open in Google Maps <ExternalLink className="h-3.5 w-3.5" /></a>
      <div className="mt-6 flex gap-2"><Clock className="mt-0.5 h-5 w-5 shrink-0" style={{ color }} />
        <dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-1 text-slate-700" data-testid="lp-hours">{studio.hours.map(([d, h]) => [<dt key={d} className="font-semibold">{d}</dt>, <dd key={`${d}h`}>{h}</dd>])}</dl></div>
      <div className="mt-8 space-y-5">{studio.directions.map(([title, text], i) => {
        const Icon = DIR_ICONS[i % DIR_ICONS.length];
        return <div key={title} className="flex gap-3"><Icon className="mt-0.5 h-5 w-5 shrink-0" strokeWidth={1.5} style={{ color }} /><div><div className="font-semibold text-slate-900">{title}</div><p className="text-slate-600">{text}</p></div></div>;
      })}</div>
      <div className="mt-8 flex flex-wrap items-start gap-3">
        <a href={wa} onClick={onWhatsApp} target="_blank" rel="noreferrer" data-testid="lp-visit-whatsapp" className="inline-flex items-center gap-2 rounded-lg px-6 py-3.5 font-semibold text-white" style={{ background: "#25D366" }}><MessageCircle className="h-5 w-5" /> WhatsApp me</a>
        <a href={`tel:${studio.phone_e164}`} onClick={onCall} data-testid="lp-call-button" className="inline-flex items-center gap-2 rounded-lg border-2 px-6 py-3 font-semibold" style={{ borderColor: color, color }}><Phone className="h-5 w-5" /> Call us {studio.phone_display}</a>
      </div>
      {freeWa && <div className="mt-5"><FreeChat href={freeWa} onClick={onWhatsApp} testid="lp-visit-free-chat" /></div>}
      <div className="mt-5"><Availability studio={studio} testid="lp-visit-availability" /></div>
    </div>
    <iframe title="map" data-testid="lp-map" className="h-full min-h-[320px] w-full rounded-2xl border-0" loading="lazy" src={`https://maps.google.com/maps?q=${encodeURIComponent(studio.address)}&output=embed`} />
  </div></section>
);
